import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { State, toDataSourceRequestString } from "@progress/kendo-data-query";
import { Observable, map } from "rxjs";
import { Flight } from "./models/flight.model";
import { FlightImage } from "./models/flight-image.model";
import { FlightSearch } from "./models/flight-search.model";
import { FlightDto, FlightImageDto, FlightEditDto, FlightPage, FlightPageDto } from "./models/flights.model";
import { environment } from "../../environments/environment";

const FLIGHTS_URL = new URL("../v2/frontend/flights", environment.serverUrl).href;
export const EXPORT_LIMIT = 10000;

@Injectable({
    providedIn: "root",
})
export class Flights2Service {
    private readonly http = inject(HttpClient);

    search(search: FlightSearch, state: State): Observable<FlightPage> {
        return this.http.get<FlightPageDto>(`${FLIGHTS_URL}?${this.queryOf(search, state)}`).pipe(map((page) => this.toPage(page)));
    }

    export(search: FlightSearch, state: State): Observable<FlightPage> {
        const all: State = { ...state, skip: 0, take: EXPORT_LIMIT };
        return this.http.get<FlightPageDto>(`${FLIGHTS_URL}/export?${this.queryOf(search, all)}`).pipe(map((page) => this.toPage(page)));
    }

    modify(request: FlightEditDto): Observable<void> {
        return this.http.post<void>(`${FLIGHTS_URL}/modify`, request);
    }

    images(flightId: number): Observable<FlightImage[]> {
        return this.http
            .get<FlightImageDto[]>(`${FLIGHTS_URL}/${flightId}/images`)
            .pipe(map((images) => images.map((image) => ({ ...image, createdAt: this.utcOf(image.createdAt) }))));
    }

    private queryOf(search: FlightSearch, state: State): string {
        const aircraft = search.helicopterIds.length ? `helicopterIds=${search.helicopterIds.join(",")}&` : "";
        return `${aircraft}from=${this.dayOf(search.from)}&to=${this.dayOf(search.to)}&${toDataSourceRequestString(state)}`;
    }

    private toPage(page: FlightPageDto): FlightPage {
        return { data: page.data.map((flight) => this.toFlight(flight)), total: page.total };
    }

    private toFlight(dto: FlightDto): Flight {
        return {
            ...dto,
            createdAt: this.utcOf(dto.createdAt),
            pilotConfirmedAt: this.utcOf(dto.pilotConfirmedAt),
            clientCreatedAt: this.utcOf(dto.clientCreatedAt),
            refuelBeforeFirstFlight: dto.refuelBeforeFirstFlight.map((refuel) => ({ ...refuel, createdAt: this.utcOf(refuel.createdAt) })),
            oilSuppliedBeforeFlight: dto.oilSuppliedBeforeFlight.map((supply) => ({ ...supply, createdAt: this.utcOf(supply.createdAt) })),
        };
    }

    /** A UTC timestamp as the backend writes it, `YYYY-MM-DD HH:mm:ss`, read as the instant it is. */
    private utcOf(timestamp: string | null): Date | null {
        return timestamp ? new Date(`${timestamp.replace(" ", "T")}Z`) : null;
    }

    /** The local calendar day, as the backend compares the dates of the flights. */
    private dayOf(date: Date): string {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    }
}
